/**
 * MessagesCanvas — MSG LIST / WAITING / compose canvas (extracted iter 69).
 *
 * Pure presentational. The parent (CrewLoginPage.js) owns all state +
 * handlers; this component renders the LEFT pane (full message view OR
 * compose form) and, in MSG LIST mode, the RIGHT pane (message list with
 * REPLY / DELETE).
 *
 * Behavior summary (preserved exactly from inline implementation):
 *   • showMessages === "waiting"  → ONE-message-at-a-time unread view (no
 *                                    right panel, no compose).
 *   • showMessages === "list"     → full LIST + DETAIL with REPLY+DELETE.
 *                                    Supervisors get a NEW MSG button.
 *   • composeMode === "reply"     → READ-ONLY To: + body textarea
 *   • composeMode === "new"       → Recipient dropdown (with [SEND ALL]) +
 *                                    body textarea
 *   • System-generated command-prompt output is merged into the same list
 *     so it surfaces alongside live inbox messages (sortedReceived).
 */
import React from "react";
import axios from "axios";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function MessagesCanvas({
  // ─── state ───
  inboxMessages,
  systemMessages,
  setInboxMessages,
  setSystemMessages,
  selectedMessageId,
  setSelectedMessageId,
  showMessages,
  composeMode,
  composeTo,
  setComposeTo,
  composeBody,
  setComposeBody,
  composeRoster,
  isSupvForMessaging,
  myUnitId,
  // ─── handlers ───
  submitCompose,
  cancelCompose,
  startNewMessage,
  startReply,
  handleMsgListDelete,
}) {
  const allMessages = [...inboxMessages, ...systemMessages];
  // Newest-first in the right list (matches operator spec; matches the
  // dispatcher's UH command output direction so the two surfaces feel
  // consistent).
  const sortedReceived = allMessages
    .slice()
    .sort((a, b) => (a.timestamp > b.timestamp ? -1 : 1));
  const selected = allMessages.find((m) => m.id === selectedMessageId) || null;

  const fmt = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${String(d.getFullYear()).slice(-2)} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    } catch { return ""; }
  };

  // ─── WAITING mode: one unread at a time ───
  const renderWaiting = () => {
    const waitingMsg = selectedMessageId
      ? [...inboxMessages, ...systemMessages].find((m) => m.id === selectedMessageId) || null
      : null;
    return (
      <div data-testid="crew-msg-waiting-pane">
        {waitingMsg ? (
          <div data-testid={`crew-msg-waiting-item-${waitingMsg.id}`}>
            <div style={{ fontSize: 14, marginBottom: 6, color: "#FFD700" }}>
              {fmt(waitingMsg.timestamp)}{"   "}
              To {waitingMsg.recipient_name || ""} from {waitingMsg.sender_name || ""}:
            </div>
            <div style={{ fontSize: 18, lineHeight: 1.4, whiteSpace: "pre-wrap", color: "#FFF" }}>
              {waitingMsg.body}
            </div>
          </div>
        ) : (
          <div style={{ color: "#888", fontSize: 16 }}>
            No new or missed messages — you're all caught up.
          </div>
        )}
      </div>
    );
  };

  // ─── COMPOSE mode (reply or new) ───
  const renderCompose = () => (
    <div data-testid="crew-msg-compose">
      <div style={{ color: "#FFD700", fontSize: 16, fontWeight: 700, marginBottom: 10 }}>
        {composeMode === "reply" ? "REPLY" : "NEW MESSAGE"}
      </div>
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 13, color: "#88BBEE" }}>TO:</div>
        {composeMode === "reply" ? (
          <input data-testid="crew-msg-compose-to-readonly" readOnly value={composeTo}
            style={{ width: "100%", background: "#111", color: "#FFF", border: "1px solid #2A4A6A", padding: 6, fontFamily: "inherit", fontSize: 16 }} />
        ) : (
          <select data-testid="crew-msg-compose-to-select" value={composeTo} onChange={(e) => setComposeTo(e.target.value)}
            style={{ width: "100%", background: "#111", color: "#FFF", border: "1px solid #2A4A6A", padding: 6, fontFamily: "inherit", fontSize: 16 }}>
            <option value="">-- SELECT RECIPIENT --</option>
            <option value="ALL">[ SEND ALL — every logged-in unit + dispatcher ]</option>
            {composeRoster.map((r) => (
              <option key={`${r.type}-${r.id}`} value={JSON.stringify({ type: r.type, id: r.id, name: r.name })}>
                {r.type === "dispatcher" ? "DISP" : "UNIT"} — {r.name}
              </option>
            ))}
          </select>
        )}
      </div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: "#88BBEE" }}>MESSAGE:</div>
        <textarea data-testid="crew-msg-compose-body" value={composeBody}
          onChange={(e) => setComposeBody(e.target.value)} rows={5}
          placeholder="Type message…"
          style={{ width: "100%", background: "#111", color: "#FFF", border: "1px solid #2A4A6A", padding: 6, fontFamily: "inherit", fontSize: 16 }} />
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button data-testid="crew-msg-compose-send" onClick={submitCompose}
          disabled={!composeBody.trim() || (composeMode === "new" && !composeTo)}
          style={{ background: (composeBody.trim() && (composeMode !== "new" || composeTo)) ? "#008f00" : "#333", color: "#FFF", border: "1px solid #00FF66", padding: "6px 22px", cursor: composeBody.trim() ? "pointer" : "not-allowed", fontSize: 14 }}>
          SEND
        </button>
        <button data-testid="crew-msg-compose-cancel" onClick={cancelCompose}
          style={{ background: "#333", color: "#FFF", border: "1px solid #888", padding: "6px 22px", cursor: "pointer", fontSize: 14 }}>
          CANCEL
        </button>
      </div>
    </div>
  );

  // ─── DETAIL view (selected msg in LIST mode) ───
  const renderDetail = () => (
    <div data-testid="crew-msg-detail">
      <div style={{ fontSize: 16, marginBottom: 6 }}>
        {fmt(selected.timestamp)}{"   "}
        To {selected.recipient_name || ""} from {selected.sender_name || ""}:
      </div>
      <div style={{ fontSize: 18, lineHeight: 1.4, whiteSpace: "pre-wrap", color: "#FFF" }}>
        {selected.body}
      </div>
    </div>
  );

  // ─── LIST row click — mark read locally + push to backend ───
  const onRowClick = (m, fromMe) => {
    setSelectedMessageId(m.id);
    // System-generated command output rows are stored only in client state
    // (no server row exists). Mark them read locally so the WAITING badge
    // clears without a 404 on the backend.
    if (!m.read_at && (m._system_local || m.sender_type === "system")) {
      setSystemMessages((prev) =>
        prev.map((x) => (x.id === m.id ? { ...x, read_at: new Date().toISOString() } : x)),
      );
    } else if (!m.read_at && !fromMe) {
      axios.put(`${API}/messages/${m.id}/read`).catch(() => {});
      setInboxMessages((prev) =>
        prev.map((x) => (x.id === m.id ? { ...x, read_at: new Date().toISOString() } : x)),
      );
    }
  };

  return (
    <div className="flex h-full" style={{ background: "#000", color: "#FFF", fontFamily: '"Courier New", monospace' }}>
      {/* LEFT pane */}
      <div data-testid="crew-msg-canvas-left"
        style={{
          flex: 1, padding: 14, overflow: "auto",
          borderRight: showMessages === "list" ? "1px solid #2A4A6A" : "none",
        }}>
        {showMessages === "waiting"
          ? renderWaiting()
          : composeMode
          ? renderCompose()
          : selected
          ? renderDetail()
          : (
            <div style={{ color: "#888", fontSize: 16 }}>
              Click a message on the right to view it here, or click MESSAGE WAITING (left rail) to view the oldest unread message.
            </div>
          )}
        {/* SUPV-only NEW MSG button — visible only in MSG LIST mode. */}
        {showMessages === "list" && isSupvForMessaging && !composeMode && (
          <button data-testid="crew-msg-new-btn" onClick={startNewMessage}
            style={{ marginTop: 18, background: "#1A4A7A", color: "#FFF", border: "1px solid #2A6AAA", padding: "6px 18px", cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>
            NEW MSG
          </button>
        )}
      </div>

      {/* RIGHT pane — visible only in MSG LIST mode. */}
      {showMessages === "list" && (
        <div data-testid="crew-msg-canvas-right" style={{ width: 420, display: "flex", flexDirection: "column", background: "#000" }}>
          <div style={{ flex: 1, overflow: "auto", padding: 4 }}>
            {sortedReceived.length === 0 && (
              <div style={{ color: "#666", fontSize: 13, padding: 8 }}>No messages</div>
            )}
            {sortedReceived.map((m) => {
              const isSel = m.id === selectedMessageId;
              const fromMe = m.sender_type === "unit" && m.sender_id === myUnitId;
              const dt = (() => {
                if (!m.timestamp) return "";
                try {
                  const d = new Date(m.timestamp);
                  const pad = (n) => String(n).padStart(2, "0");
                  return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
                } catch { return ""; }
              })();
              const dirLabel = fromMe ? "TO" : "FROM";
              const partyId = fromMe ? (m.recipient_name || "—") : (m.sender_name || "—");
              return (
                <div key={m.id} data-testid={`crew-msg-list-row-${m.id}`}
                  onClick={() => onRowClick(m, fromMe)}
                  style={{
                    background: isSel ? "#1A4A7A" : "transparent",
                    color: isSel ? "#FFF" : "#DDD",
                    padding: "4px 6px",
                    cursor: "pointer",
                    fontSize: 12,
                    borderBottom: "1px solid #1A2A3A",
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}>
                  {dt} {dirLabel}: {partyId} - {(m.body || "").slice(0, 60)}
                </div>
              );
            })}
          </div>
          {/* REPLY + DELETE buttons */}
          <div style={{ display: "flex", borderTop: "1px solid #2A4A6A" }}>
            <button data-testid="crew-msg-reply-btn" onClick={startReply}
              disabled={!isSupvForMessaging || !selected || (selected && selected.sender_type === "unit" && selected.sender_id === myUnitId)}
              style={{
                flex: 1, background: "#222", color: isSupvForMessaging ? "#FFF" : "#666",
                border: "1px solid #2A4A6A", padding: "6px 0", cursor: isSupvForMessaging ? "pointer" : "not-allowed",
                fontSize: 14, fontFamily: "inherit",
              }}>
              REPLY
            </button>
            <button data-testid="crew-msg-delete-btn"
              onClick={() => selected && handleMsgListDelete(selected)}
              disabled={!selected}
              style={{
                flex: 1, background: "#222", color: "#FFF",
                border: "1px solid #2A4A6A", borderLeft: "none",
                padding: "6px 0", cursor: selected ? "pointer" : "not-allowed",
                fontSize: 14, fontFamily: "inherit",
              }}>
              DELETE
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default MessagesCanvas;
